<template>
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- 배경 오버레이 -->
      <div class="absolute inset-0 bg-black bg-opacity-50" @click="close"></div>
      
      <!-- 모달 컨텐츠 -->
      <div class="relative bg-white rounded-lg w-full max-w-[800px] mx-4 max-h-[80vh] overflow-y-auto p-6">
        <!-- 닫기 버튼 -->
        <button 
          class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          @click="close"
        >
          ✕
        </button>
  
        <!-- 모달 헤더 -->
        <h2 class="text-xl font-bold mb-4">추천 프롬프트</h2>
  
        <!-- 프롬프트 목록 -->
        <div class="space-y-4">
          <div 
            v-for="(prompt, index) in prompts" 
            :key="index" 
            class="border rounded-lg p-4 text-left"
          >
            <h3 class="font-bold text-lg mb-2">{{ prompt.title }}</h3>
            <div class="space-y-2">
              <div 
                v-for="(section, sIndex) in prompt.sections" 
                :key="sIndex"
                class="ml-4"
              >
                <h4 :class="[section.titleClass || 'font-semibold']">{{ section.title }}</h4>
                <p v-if="section.content" class="text-sm text-gray-600 ml-2">{{ section.content }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref } from 'vue';
  
  const isOpen = ref(false);
  
  const prompts = ref([
  {
    title: '1. 보도자료',
    sections: [
      {
        title: '아래와 같은 양식으로 보도자료 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a professional PR content writer.'
      },
      {
        title: 'Context(상황)',
        content: `작성할 보도자료의 주제: [제품 출시/서비스 업데이트/이벤트 발표 등 구체적 상황 입력]
주요 타겟 독자: [예: 기자, 업계 전문가, 대중]
필요한 톤: [공식적, 친근함, 전문적 등]`
      },
      {
        title: 'Input Values(입력값)',
        content: `보도자료 제목: [ex. 혁신적인 AI 기술을 도입한 새로운 앱 출시]
주요 메시지: [ex. 앱의 주요 기능 및 장점]
배포 목적: [ex. 브랜드 인지도 향상, 신규 고객 유치]
CTA(Call to Action): [ex. 홈페이지 방문, 데모 신청]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 제목과 부제목을 짧고 강렬하게 작성합니다.
2. 리드(첫 문장 또는 첫 단락)에서 핵심 메시지를 전달합니다.
3. 중간 본문에서 주요 기능, 장점, 시장 동향 또는 배경을 설명합니다.
4. 독자의 행동을 유도하는 Call to Action(CTA)을 포함합니다.
5. 언론사가 쉽게 편집할 수 있도록 간결한 문장으로 작성합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 분량: 300~500 단어 이내
- 전문 용어 사용은 최소화하고 쉽게 이해할 수 있는 언어로 작성
- 긍정적이고 희망적인 톤 유지`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Title: 보도자료 제목
- Subtitle: 부제목
- Lead: 핵심 메시지
- Body: 본문(기능, 장점, 배경 설명)
- CTA: 행동 유도 메시지`
      }
    ]
  },
  {
    title: '2. SNS 콘텐츠 플래닝',
    sections: [
      {
        title: '아래와 같은 양식으로 SNS 콘텐츠 플래닝 해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a social media strategist.'
      },
      {
        title: 'Context(상황)',
        content: `플랫폼: [ex. Instagram, Twitter, LinkedIn]
타겟 사용자: [ex. 20대 소비자, B2B 고객]
목표: [브랜드 인지도 상승, 판매 증가, 팔로워 참여]`
      },
      {
        title: 'Input Values(입력값)',
        content: `주제: [예: 신제품 출시, 브랜드 캠페인]
주요 메시지: [예: 혁신성, 사용자 혜택]
포스팅 형식: [예: 카드 뉴스, 짧은 동영상, 인포그래픽]
해시태그 스타일: [캐주얼, 트렌디, 공식적]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 각 플랫폼의 특성을 고려해 메시지를 간결하고 강렬하게 작성합니다.
2. 이미지와 어울리는 짧고 기억에 남는 캡션을 만듭니다.
3. 관련성과 트렌드를 반영한 해시태그를 포함합니다.
4. 타겟 오디언스의 흥미를 끌 수 있는 CTA를 추가합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 캡션은 각 플랫폼의 글자 제한에 맞추기 (예: Instagram 2,200자, Twitter 280자)
- 비주얼 요소와 텍스트가 조화를 이루도록 설계`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Platform: 대상 플랫폼
- Caption: 캡션 텍스트
- Hashtags: 추천 해시태그
- CTA: 행동 유도 메시지`
      }
    ]
  },
  
  {
    title: '3. 이벤트 홍보 글쓰기',
    sections: [
      {
        title: '아래와 같은 양식으로 이벤트 홍보 글 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as an event marketing copywriter.'
      },
      {
        title: 'Context(상황)',
        content: `이벤트 유형: [예: 오프라인 세미나, 온라인 웨비나, 플래시 세일]
타겟 관객: [예: 기술 전문가, 일반 소비자]
목표: [참여자 수 증가, 브랜드 관심 유도]`
      },
      {
        title: 'Input Values(입력값)',
        content: `이벤트 이름: [예: 'AI 기술 혁신 웨비나 2024']
날짜/시간/장소: [예: 2024년 12월 10일, 온라인 Zoom]
주요 혜택: [예: 최신 기술 인사이트, 무료 참여]
등록 방법: [예: 링크 클릭, QR 코드 스캔]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 이벤트의 핵심 가치를 한 줄로 요약합니다.
2. 일정과 장소, 등록 절차를 명확히 전달합니다.
3. 참여 혜택을 강조하여 독자의 흥미를 유발합니다.
4. CTA를 포함하여 행동을 유도합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 지나치게 긴 문장은 피하고, 읽기 쉽게 작성
- 긍정적이고 설득력 있는 어조 유지`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Event Name: 이벤트 이름
- Key Highlights: 주요 혜택
- Details: 날짜, 시간, 장소
- CTA: 등록 방법 및 행동 유도 메시지`
      }
    ]
  },
  {
    title: '4. 제품 설명 문구 작성',
    sections: [
      {
        title: '아래와 같은 양식으로 제품 설명 문구 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a marketing copywriter.'
      },
      {
        title: 'Context(상황)',
        content: `제품 유형: [예: 전자 기기, 화장품, 소프트웨어]
타겟 소비자: [예: 기술 애호가, 젊은 여성, 기업 고객]
목적: [브랜드 이미지 강화, 판매 전환]`
      },
      {
        title: 'Input Values(입력값)',
        content: `제품 이름: [예: 스마트폰 모델명, 뷰티 제품 이름]
주요 특징: [예: AI 지원, 고급 성분]
사용자의 주요 관심사: [예: 편리성, 내구성, 비용 효율]
Tone: [예: 친근하고 설득력 있는 톤]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 제품의 주요 이점을 간결하게 요약합니다.
2. 소비자에게 와닿는 사례나 용도를 설명합니다.
3. 경쟁 제품과의 차별성을 강조합니다.
4. CTA(Call to Action)를 포함하여 구매 또는 체험을 유도합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 너무 기술적이거나 복잡한 용어는 지양
- 길이는 100~150단어`
      }
    ]
  },
  {
    title: '5. 블로그 작성 가이드',
    sections: [
      {
        title: '아래와 같은 양식으로 블로그 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a blog content strategist and writer.'
      },
      {
        title: 'Context(상황)',
        content: `블로그 주제: [예: 여행, 기술 리뷰, 자기 계발, 마케팅 팁 등]
타겟 독자: [예: 20~30대 여행 애호가, 기술 전문가, 창업자]
목표: [예: SEO 강화, 브랜드 신뢰도 구축, 판매로 연결]`
      },
      {
        title: 'Input Values(입력값)',
        content: `주제 키워드: [예: '최고의 여행 가방', '2024년 웹 개발 트렌드']
글 스타일: [예: 친근함, 전문가적, 이야기 중심]
글 길이: [예: 800~1200 단어]
추가 요구사항: [예: 외부 링크 포함, 이미지 추천]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. SEO 키워드를 활용하여 매력적인 제목과 소제목을 작성합니다.
2. 도입부에서 독자의 관심을 끌고, 주제의 핵심을 요약합니다.
3. 본문에서 실질적인 정보, 팁, 또는 사례를 제공하여 가치를 높입니다.
4. 결론에 행동 유도(Call to Action)를 추가합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 지나치게 긴 문장은 피하고 간결한 문체 유지
- 중복된 내용 피하기`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Title: 글 제목
- Subheadings: 소제목 목록
- Body: 본문 내용
- CTA: 행동 유도 메시지`
      }
    ]
  },
  {
    title: '6. 제품 설명 홍보 가이드',
    sections: [
      {
        title: '아래와 같은 양식으로 제품 홍보 문구 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a product marketing copywriter.'
      },
      {
        title: 'Context(상황)',
        content: `제품 카테고리: [예: 전자 기기, 의류, 소프트웨어, 뷰티 제품]
타겟 고객: [예: 기술 애호가, 친환경 소비자, 부모님]
목표: [브랜드 이미지 구축, 판매 전환, 기능 강조]`
      },
      {
        title: 'Input Values(입력값)',
        content: `제품 이름: [예: 'GreenFresh 스마트 공기청정기']
주요 기능: [예: 실시간 공기 질 측정, 앱 제어 가능]
고객에게 주는 이점: [예: 건강한 생활 환경, 사용 편리성]
Tone: [예: 신뢰감 있는, 혁신적인, 따뜻한]
CTA(Call to Action): [예: '지금 구매하기', '무료 데모 체험']`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 제품의 가장 큰 장점을 강조하는 강렬한 문구로 시작합니다.
2. 고객의 문제를 해결하는 제품의 핵심 기능과 이점을 설명합니다.
3. 경쟁 제품과의 차별화를 나타내는 특성을 포함합니다.
4. 명확하고 매력적인 행동 유도 메시지(CTA)를 추가합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 복잡한 기술 용어는 쉽게 풀어 설명
- 길이는 150~200단어 이내로 유지
- 긍정적이고 신뢰감을 주는 톤`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Product Name: 제품 이름
- Tagline: 제품 슬로건
- Description: 상세 설명
- CTA: 행동 유도 메시지`
      }
    ]
  },{
    title: '7. 팝업스토어 홍보 가이드',
    sections: [
      {
        title: '아래와 같은 양식으로 팝업스토어 홍보 문구 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a marketing copywriter specializing in event promotions.'
      },
      {
        title: 'Context(상황)',
        content: `팝업스토어 주제: [예: 신제품 체험, 브랜드 홍보, 한정 판매]
위치와 시간: [예: 서울 강남, 2024년 12월 1~15일]
목표: [방문자 유치, 브랜드 인지도 상승, 구매 전환]`
      },
      {
        title: 'Input Values(입력값)',
        content: `이벤트 이름: [예: 'Horizon Cafe 팝업스토어']
주요 특징: [예: 한정판 제품, 체험 공간, 특별 할인]
타겟 고객: [예: 젊은 소비자, 지역 주민, 패션 애호가]
추가 요소: [예: 무료 기념품, SNS 이벤트, 유명 인사 초대]`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `1. 팝업스토어의 한정성(기간, 특별 혜택)을 강조하는 문구로 시작합니다.
2. 방문자들이 기대할 수 있는 경험과 혜택을 구체적으로 설명합니다.
3. 장소와 시간을 명확히 전달합니다.
4. 방문을 유도하는 행동 유도 메시지(CTA)를 포함합니다.
5. SNS 공유를 유도하는 해시태그나 이벤트 참여 안내를 추가합니다.`
      },
      {
        title: 'Constraints(제약사항)',
        content: `- 문장은 간결하고 명확하게 유지
- 브랜드의 이미지와 톤에 맞는 언어 사용
- 길이는 100~150단어`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
- Event Name: 팝업스토어 이름
- Highlights: 주요 특징
- Details: 시간, 장소 등 구체적인 정보
- CTA: 방문 유도 메시지`
      }
    ]
  },
  {
    title: '8. 제안서 작성 가이드',
    sections: [
      {
        title: '아래와 같은 양식으로 제안서 작성해줘',
        content: '',
        titleClass: 'text-red-500'
      },
      {
        title: 'Role(역할 지정)',
        content: 'Act as a professional project proposal writer'
      },
      {
        title: 'Context(상황)',
        content: `- 사용자는 특정 프로젝트를 위한 제안서를 작성하려고 합니다.
- 목표는 프로젝트의 주요 아이디어를 효과적으로 전달하고, 제안서 수신자가 이를 승인하도록 설득하는 것입니다.`
      },
      {
        title: 'Input Values(입력값)',
        content: `- 프로젝트의 제목
- 프로젝트의 주요 목적 및 목표
- 프로젝트의 주요 단계 및 일정
- 필요한 예산 및 자원
- 예상 성과 및 기대 효과`
      },
      {
        title: 'Instructions(단계별 지시사항)',
        content: `- 입력된 프로젝트의 제목과 목적을 기반으로 제안서의 개요를 작성합니다.
- 프로젝트 주요 내용을 포함하여 Output fields에 맞게 제안서를 작성합니다.
- 수신자가 쉽게 이해할 수 있는 간결한 문장과 정돈된 형식을 사용합니다.`
      },
      {
        title: 'Output Format',
        content: `Output Format: Markdown

Output Fields:
01 제안개요
   - 제안의 목적
   - 제안 운영 로드맵
   - 사업 기대효과
02 제안업체 일반현황
   - 일반현황
   - 조직 및 인원
   - 주요 사업 내용
   - 주요 사업 실적
03 사업 내용 부문
   - 상황분석 및 시사점
   - 컨셉 및 전략 도출
   - 이슈화 프로그램
   - 정기프로그램
   - SNS 프로그램
04 사업 관리 부문
   - 추진 일정 계획
   - 투입인력 및 이력사항
   - 위기 관리 방안
   - 보고 및 검토 계획
   - 품질 관리 계획`
      }
    ]
  }
]);

const close = () => {
  isOpen.value = false;
};

const open = () => {
  isOpen.value = true;
};

defineExpose({ open, close });
</script>

<style scoped>
.modal-content {
  white-space: pre-wrap;
}

/* 스크롤바 스타일링 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>